<template>
    <config-section
        :title="$t('auth.api_keys.heading_title')"
        :description="$t('auth.api_keys.heading_description')"
    >
        <div>
            <h2 class="text-body mb-8 first-letter:uppercase">{{ $t('auth.api_keys.title') }}</h2>
            <p class="text-primary-80" v-html="$t('auth.api_keys.description')"/>

            <div class="pt-24" v-if="tokens">
                <div
                    v-for="token in tokens"
                    :key="'user-token-' + token.id"
                    class="flex items-center justify-between bg-white odd:bg-primary-5 h-32 pl-16"
                >
                    <span>{{ token.name }}</span>
                    <div class="flex">
                        <span class="mr-16">{{ $t('auth.api_keys.created_at', { date: formatDate(token.created_at) }) }}</span>
                        <simple-dropdown position="right" without-chevron>
                            <template #trigger>
                                <button class="mr-16 group">
                                    <svg-icon type="delete" class="text-primary-20 group-hover:text-semantic-error trans-ease max-w-16 max-h-16"></svg-icon>
                                </button>
                            </template>
                            <template #content>
                                <div class="shadow bg-white rounded">
                                    <dropdown-item
                                        :input-label="$t('general.delete')"
                                        danger
                                        @click="deleteToken(token)"
                                    />
                                </div>
                            </template>
                        </simple-dropdown>
                    </div>
                </div>
            </div>
        </div>

        <template #buttons>
            <simple-button
                display="secondary"
                :loading="generatingToken"
                look="primary"
                @click="addingNewToken = true"
            >{{ $t('auth.api_keys.create_key') }}</simple-button>
        </template>

        <popup-modal v-if="addingNewToken" :title="$t('auth.api_keys.create_key')" custom-classes="w-600" @close="addingNewToken = false">
            <simple-input v-model="form.name" class="mb-16">
                {{ $t('auth.api_keys.name') }}
            </simple-input>

            <div v-if="newlyCreatedToken" class="space-y-12">
                <copy-to-clipboard
                    :text="newlyCreatedToken.token"
                />

                <banner
                    :description="$t('auth.api_keys.warning')"
                    inline
                    warning
                />

            </div>

            <simple-button
                v-if="!newlyCreatedToken"
                @click="createToken"
                display="secondary"
                look="primary"
                :loading="generatingToken"
                :disabled="!form.isDirty()"
            >{{ $t('auth.api_keys.generate_key') }}</simple-button>

            <simple-button
                v-else
                @click="addingNewToken = false"
                display="secondary"
                class="ml-auto"
            >{{ $t('general.close') }}</simple-button>
        </popup-modal>
    </config-section>
</template>

<script>
import ConfigurationSection from './ConfigurationSection.vue';
import {mapState} from 'pinia';
import {useAuthenticationStore} from '../state/modules/authentication';
import moment from 'moment';
import SimpleButton from './ui/SimpleButton.vue';
import SvgIcon from './ui/SvgIcon.vue';
import SimpleDropdown from "./ui/SimpleDropdown";
import PopupModal from "./ui/PopupModal";
import Input from "./forms/Input";
import Form from "../etc/forms/Form";
import Banner from "./ui/Banner.vue";
import CopyToClipboard from "./ui/CopyToClipboard.vue";
import DropdownItem from "./ui/DropdownItem.vue";

export default {
    components: {
        DropdownItem,
        CopyToClipboard,
        Banner,
        'simple-input': Input,
        PopupModal,
        SimpleDropdown,
        'config-section': ConfigurationSection,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
    },
    data: function () {
        return {
            addingNewToken: false,
            tokens: null,
            newlyCreatedToken: null,
            generatingToken: false,
            deletingToken: false,
            form: this.generateForm()
        }
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentUser'])
    },
    methods: {
        fetchTokens () {
            return axios.get(route('app.user-tokens.index')).then(({data}) => {
                this.tokens = data;
            })
        },
        createToken () {
            this.generatingToken = true;

            this.form.submit(data => {
                return axios.post(route('app.user-tokens.store'), data).then(({ data }) => {
                    this.newlyCreatedToken = data;
                    this.tokens.push(data);
                    this.generatingToken = false;
                })
            })
        },
        deleteToken (token) {
            this.deletingToken = true;
            axios.delete(route('app.user-tokens.destroy', {id: token.id})).then(() => {
                this.tokens = this.tokens.filter(i => i.id !== token.id);
                this.deletingToken = false;
            })
        },
        refreshToken () {

        },
        formatDate(date) {
            return moment(date).format('DD-MM-YYYY')
        },
        generateForm() {
            return new Form([
                { name: 'name', value: '' }
            ]);
        }
    },
    watch: {
        addingNewToken(val) {
            if (!val) {
                this.newlyCreatedToken = null;
                this.form = this.generateForm();
            }
        }
    },
    mounted() {
        let request = this.fetchTokens();

        if (this.$route.query.create_token) {
            this.form.name = this.$route.query.create_token;
            this.addingNewToken = true;
            request.then(() => {
                this.createToken();

                let query = Object.assign({}, this.$route.query);
                delete query.create_token;
                this.$router.replace({ query });
            })
        }
    }
}
</script>
